import React, { useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiEdit2, FiMail, FiUserX, FiUserCheck, FiShield, FiGrid } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import { useResendInviteMutation } from "../hooks/useResendInviteMutation";
import { useReactivateUserMutation } from "../hooks/useReactivateUserMutation";
import { useCurrentUserId } from "hooks/auth";
import type { UserListItem } from "../../../types/userInvite";

interface UserRowActionsProps {
  user: UserListItem;
  onDeactivate: (user: UserListItem) => void;
  onEdit: (user: UserListItem) => void;
  // PRO-413: RBAC v2 additive layer — "Manage roles" opens the AssignRoleDrawer.
  rbacEnabled?: boolean;
  onManageRoles?: (user: UserListItem) => void;
  // PRO-413 #UM1: "View access" opens the read-only effective-access matrix.
  onViewAccess?: (user: UserListItem) => void;
}

interface ActionButtonProps {
  colorClass?: string;
  "data-testid"?: string;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
}

const ActionButton = ({
  onClick,
  icon,
  title,
  colorClass = "text-gray-500 hover:text-gray-700",
  disabled = false,
  ...rest
}: ActionButtonProps) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title={title}
    disabled={disabled}
    className={`rounded p-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${colorClass}`}
    {...rest}
  >
    {icon}
  </button>
);

type ConfirmAction = "resend" | "reactivate";

export const UserRowActions = ({
  user,
  onEdit,
  onDeactivate,
  rbacEnabled = false,
  onManageRoles,
  onViewAccess,
}: UserRowActionsProps) => {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const currentUserId = useCurrentUserId();

  const resend = useResendInviteMutation();
  const reactivate = useReactivateUserMutation();

  const isPending = resend.isPending || reactivate.isPending;

  const isOwnUser = user.id === currentUserId;
  const canResend = user.status === "invited";
  const canDeactivate = !isOwnUser && (user.status === "active" || user.status === "invited");
  const canReactivate = user.status === "deactivated";

  // Negative IDs are tour-fixture dummy users — never fire real mutations.
  const isDummy = user.id < 0;

  const handleConfirm = () => {
    if (isDummy) {
      setConfirmAction(null);
      return;
    }
    if (confirmAction === "resend") {
      resend.mutate(
        { userId: user.id },
        { onSettled: () => setConfirmAction(null) }
      );
    } else if (confirmAction === "reactivate") {
      reactivate.mutate(user.id, {
        onSettled: () => setConfirmAction(null),
      });
    }
  };

  const modalConfig = {
    resend: {
      title: "Resend invite?",
      description: `A new invite will be sent to ${user.email}. The invite link expires in 72 hours.`,
      confirmText: "Resend invite",
    },
    reactivate: {
      title: `Reactivate ${user.first_name}?`,
      description: "A new invite will be sent to this user. They will be able to log in once they complete registration.",
      confirmText: "Reactivate",
    },
  };

  const activeModal = confirmAction ? modalConfig[confirmAction] : null;

  return (
    <>
      <ReadOnlyGuard>
      <div className="flex items-center justify-end gap-1">
        {rbacEnabled && onManageRoles && (
          <ActionButton
            onClick={() => onManageRoles(user)}
            icon={<FiShield size={14} />}
            title="Manage roles"
            colorClass="text-gray-500 hover:text-blue-600"
            data-testid={`user-action-manage-roles-${user.id}`}
            disabled={isPending}
          />
        )}
        {rbacEnabled && onViewAccess && (
          <ActionButton
            onClick={() => onViewAccess(user)}
            icon={<FiGrid size={14} />}
            title="View access"
            colorClass="text-gray-500 hover:text-blue-600"
            data-testid={`user-action-view-access-${user.id}`}
            disabled={isPending}
          />
        )}
        <ActionButton
          onClick={() => onEdit(user)}
          icon={<FiEdit2 size={14} />}
          title="Edit user"
          data-testid={`user-action-edit-${user.id}`}
          disabled={isPending}
        />
        {canResend && (
          <ActionButton
            onClick={() => setConfirmAction("resend")}
            icon={isPending && confirmAction === "resend"
              ? <Spinner size="xs" />
              : <FiMail size={14} />}
            title="Resend invite"
            data-testid={`user-action-resend-${user.id}`}
            disabled={isPending}
          />
        )}
        {canDeactivate && (
          <ActionButton
            onClick={() => onDeactivate(user)}
            icon={<FiUserX size={14} />}
            title="Deactivate user"
            colorClass="text-red-500 hover:text-red-700"
            data-testid={`user-action-deactivate-${user.id}`}
            disabled={isPending}
          />
        )}
        {canReactivate && (
          <ActionButton
            onClick={() => setConfirmAction("reactivate")}
            icon={isPending && confirmAction === "reactivate"
              ? <Spinner size="xs" />
              : <FiUserCheck size={14} />}
            title="Reactivate user"
            colorClass="text-gray-500 hover:text-green-600"
            data-testid={`user-action-reactivate-${user.id}`}
            disabled={isPending}
          />
        )}
      </div>
      </ReadOnlyGuard>

      {activeModal && (
        <Modal
          isOpen
          modalType="alert"
          iconType={confirmAction === "resend" ? "mail" : "info"}
          title={activeModal.title}
          description={activeModal.description}
          primaryButton={{
            text: activeModal.confirmText,
            isLoading: isPending,
            disabled: isPending,
            onClick: handleConfirm,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setConfirmAction(null),
          }}
          onClose={() => setConfirmAction(null)}
        >
          <></>
        </Modal>
      )}
    </>
  );
};
